import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SiteButton } from "@/components/brand/SiteButton";
import { Container, Section, SectionHeading } from "@/components/layout/Section";
import { HERO, INDUSTRIES, INTRO, PROCESS_STEPS } from "@/config/homeContent";
import { SITE } from "@/config/site";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Intro = () => (
  <Section testid="intro-section">
    <Container>
      <motion.div
        variants={reveal}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-60px" }}
        className="grid gap-10 lg:grid-cols-12 lg:gap-16"
      >
        <div className="space-y-4 lg:col-span-5">
          <p className="eyebrow">{INTRO.eyebrow}</p>
          <h2 className="font-display text-2xl font-bold tracking-tight text-brand-snow sm:text-3xl lg:text-4xl">
            {INTRO.title}
          </h2>
        </div>
        <div className="space-y-5 lg:col-span-7">
          {INTRO.paragraphs.map((p) => (
            <p key={p.slice(0, 24)} className="text-base leading-relaxed text-brand-mist">
              {p}
            </p>
          ))}
        </div>
      </motion.div>
    </Container>
  </Section>
);

export const Process = () => (
  <Section testid="process-section" className="border-t border-brand-mist/10">
    <Container className="space-y-12">
      <SectionHeading
        eyebrow="How we work"
        title="A process built for momentum"
        description="Four phases, weekly visibility, and a launch that behaves like a checklist rather than a leap of faith."
        testid="process-heading"
      />
      <ol className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="process-steps">
        {PROCESS_STEPS.map((s, i) => (
          <motion.li
            key={s.step}
            variants={reveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            custom={i}
            className="surface-card relative space-y-4 p-6"
            data-testid={`process-step-${s.step}`}
          >
            <span className="font-display text-3xl font-bold text-brand-emerald/80">{s.step}</span>
            <h3 className="font-display text-lg font-semibold text-brand-snow">{s.title}</h3>
            <p className="text-sm leading-relaxed text-brand-mist">{s.text}</p>
          </motion.li>
        ))}
      </ol>
    </Container>
  </Section>
);

export const Industries = () => (
  <Section testid="industries-section" className="border-t border-brand-mist/10">
    <Container className="grid gap-10 lg:grid-cols-12 lg:gap-16">
      <div className="lg:col-span-5">
        <SectionHeading
          eyebrow="Who we build for"
          title="Sector-agnostic, outcome-specific"
          description="We design and build for teams across these sectors — adapting the stack and strategy to the way each market actually buys."
          testid="industries-heading"
        />
      </div>
      <div className="lg:col-span-7">
        <motion.ul
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="flex flex-wrap gap-3"
          data-testid="industries-list"
        >
          {INDUSTRIES.map((ind) => (
            <li
              key={ind}
              className="rounded-full border border-brand-mist/15 bg-brand-charcoal px-5 py-2.5 text-sm text-brand-snow transition-colors duration-200 hover:border-brand-emerald/40 hover:text-brand-emerald"
            >
              {ind}
            </li>
          ))}
        </motion.ul>
      </div>
    </Container>
  </Section>
);

export const CtaBand = () => (
  <Section testid="cta-band-section" className="border-t border-brand-mist/10">
    <Container>
      <motion.div
        variants={reveal}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-60px" }}
        className="relative overflow-hidden rounded-2xl border border-brand-emerald/20 bg-gradient-to-br from-brand-charcoal via-brand-charcoal to-brand-black p-10 sm:p-14"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(36rem 18rem at 85% 0%, rgba(56,139,255,0.12), transparent 60%), radial-gradient(30rem 16rem at 0% 100%, rgba(22,214,154,0.10), transparent 60%)",
          }}
        />
        <div className="relative flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-xl space-y-4">
            <p className="eyebrow">Start the conversation</p>
            <h2 className="font-display text-2xl font-bold tracking-tight text-brand-snow sm:text-3xl lg:text-4xl">
              Tell us what you're building. We'll tell you honestly what it takes.
            </h2>
            <p className="text-sm leading-relaxed text-brand-mist sm:text-base">
              A free discovery call with a senior engineer or strategist — scope, timeline and a straight answer
              on whether we're the right fit.
            </p>
          </div>
          <div className="flex shrink-0 flex-col gap-4 sm:flex-row lg:flex-col">
            <SiteButton to={HERO.primaryCta.to} testid="cta-band-discovery-call" size="lg">
              {HERO.primaryCta.label}
              <ArrowRight size={16} />
            </SiteButton>
            <SiteButton href={`mailto:${SITE.email}`} variant="outline" testid="cta-band-email" size="lg">
              {SITE.email}
            </SiteButton>
          </div>
        </div>
      </motion.div>
    </Container>
  </Section>
);
